import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, StaggerReveal, Eyebrow, TiltCard } from "../ui";
import { API_BASE_URL, ENDPOINTS } from "../../config/api";
import { formatMoney } from "../../utils/format";
import { authFetch, getUser } from "../../utils/auth";
import { getTierLevel, isSubscriptionActive } from "../../utils/subscription";

const PLAN_META = [
  {
    name: "Basic",
    backendPlanId: "BASIC",
    tag: null,
    features: [
      "Browse profiles",
      "Basic filters",
      "3 swipes per day",
      "Limited likes",
    ],
    btnClass: "border border-black/15 text-black hover:bg-black/4",
    btnLabel: "Get started",
    cardClass: "bg-blush",
  },
  {
    name: "Standard",
    backendPlanId: "STANDARD",
    tag: "Most popular",
    features: [
      "Browse more profiles",
      "See who liked you",
      "Advanced filters",
      "Unlimited likes & messages",
    ],
    btnClass: "bg-magenta text-white hover:opacity-90",
    btnLabel: "Subscribe now",
    cardClass: "bg-black",
  },
  {
    name: "Premium",
    backendPlanId: "PREMIUM",
    tag: null,
    features: [
      "Everything in Connect",
      "Manual matchmaking",
      "Max & Sherry curation",
      "Verified profile badge",
    ],
    btnClass: "text-[#1a0f05] hover:opacity-90",
    btnLabel: "Get Premium",
    btnStyle: { background: "linear-gradient(135deg, #c9a040, #e8c060)" },
    cardClass: "",
    cardStyle: {
      background: "linear-gradient(135deg, #1a0f20 0%, #0a0008 100%)",
      border: "1.5px solid rgba(208,32,168,0.3)",
    },
    priceColor: "text-[#d4a847]",
  },
];

export default function Pricing() {
  const [prices, setPrices] = useState<Record<string, string>>({});
  const currentUser = getUser();

  const rawTier = currentUser?.subscription?.tier || currentUser?.role || "";
  const currentLevel = getTierLevel(rawTier);
  const isActive = isSubscriptionActive(currentUser);

  useEffect(() => {
    let cancelled = false;
    async function loadPlans() {
      try {
        const res = await authFetch(
          `${API_BASE_URL}${ENDPOINTS.SUBSCRIPTION_PLANS}`,
          {},
          10000,
        );
        if (!res.ok) return;
        const json = await res.json();
        const list = Array.isArray(json)
          ? json
          : Array.isArray(json?.data)
            ? json.data
            : [];

        const next: Record<string, string> = {};
        for (const meta of PLAN_META) {
          const match = list.find(
            (p: any) =>
              p.id === meta.backendPlanId ||
              p.slug?.toUpperCase() === meta.backendPlanId,
          );
          if (match?.price !== undefined) {
            next[meta.name] = formatMoney(match.price, match.currency || "MWK");
          }
        }
        if (!cancelled) setPrices(next);
      } catch {}
    }
    loadPlans();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="py-16 lg:py-[100px] px-[5%] bg-white" id="pricing">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <Eyebrow>Pricing</Eyebrow>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="text-[clamp(28px,4vw,44px)] font-bold tracking-[-1.2px] mb-8 lg:mb-16">
            Choose your plan
          </h2>
        </Reveal>

        {isActive && (
          <Reveal delay={150}>
            <p className="text-sm text-muted -mt-6 mb-10 lg:-mt-12 lg:mb-14">
              You can upgrade any time. Downgrades unlock once your current plan
              expires.
            </p>
          </Reveal>
        )}

        <StaggerReveal className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {PLAN_META.map((plan) => {
            const live = prices[plan.name];
            const numericPart = live ? live.replace(/^MK/, "") : null;

            const planLevel = getTierLevel(plan.backendPlanId);
            const isCurrent = currentLevel > 0 && currentLevel === planLevel;
            const isDowngrade = isActive && planLevel < currentLevel;
            const isUpgrade = isActive && planLevel > currentLevel;

            return (
              <TiltCard
                key={plan.name}
                className={`flex flex-col h-full rounded-[24px] p-6 lg:p-10 border-2 ${
                  isCurrent
                    ? "border-green-500 shadow-green-500/20 shadow-xl"
                    : isDowngrade
                      ? "border-transparent opacity-50 grayscale"
                      : "border-transparent"
                } ${plan.cardClass}`}
                style={plan.cardStyle as React.CSSProperties}
              >
                <div className="flex justify-between items-start mb-6">
                  {plan.tag && (
                    <div className="inline-block bg-magenta text-white text-[10px] font-bold tracking-[1.5px] uppercase px-3 py-1 rounded-full">
                      {plan.tag}
                    </div>
                  )}
                  {isCurrent && (
                    <div className="inline-block bg-green-500 text-white text-[10px] font-bold tracking-[1.5px] uppercase px-3 py-1 rounded-full ml-auto">
                      Current Plan
                    </div>
                  )}
                </div>

                <div
                  className={`text-[26px] font-bold tracking-tight mb-2 ${plan.cardClass === "bg-black" || plan.cardStyle ? "text-white" : "text-black"}`}
                >
                  {plan.name}
                </div>
                <div className="flex items-baseline gap-1 mb-6 lg:mb-8 flex-wrap">
                  <span
                    className={`text-sm font-medium ${plan.cardClass === "bg-black" || plan.cardStyle ? "text-white/40" : "text-muted"}`}
                  >
                    MWK
                  </span>
                  <span
                    className={`text-4xl lg:text-5xl font-bold tracking-[-2px] leading-none ${
                      isDowngrade
                        ? "line-through decoration-red-500 decoration-2"
                        : ""
                    } ${plan.priceColor || (plan.cardClass === "bg-black" || plan.cardStyle ? "text-white" : "text-black")}`}
                  >
                    {numericPart ?? (
                      <span className="inline-block opacity-30">—</span>
                    )}
                  </span>
                  <span
                    className={`text-sm ${plan.cardClass === "bg-black" || plan.cardStyle ? "text-white/35" : "text-muted"}`}
                  >
                    /month
                  </span>
                </div>

                <ul className="space-y-3 mb-6 lg:mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className={`flex items-center gap-2.5 text-sm ${plan.cardClass === "bg-black" || plan.cardStyle ? "text-white/60" : "text-muted"}`}
                    >
                      <div
                        className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(208,32,168,0.15)" }}
                      >
                        <svg
                          viewBox="0 0 12 12"
                          fill="none"
                          stroke="#d020a8"
                          strokeWidth="2.5"
                          className="w-2"
                        >
                          <polyline points="2,6 5,9 10,3" />
                        </svg>
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>

                {isDowngrade ? (
                  <div className="block text-center py-3.5 rounded-[14px] text-[15px] font-semibold bg-neutral-200 text-neutral-500 cursor-not-allowed mt-auto">
                    Downgrade unavailable
                  </div>
                ) : isCurrent ? (
                  <div className="block text-center py-3.5 rounded-[14px] text-[15px] font-semibold bg-green-600 text-white opacity-100 cursor-default mt-auto">
                    Current Plan
                  </div>
                ) : (
                  <Link
                    to="/pay"
                    state={{ preselectPlanId: plan.backendPlanId }}
                    className={`block text-center py-3.5 rounded-[14px] text-[15px] font-semibold no-underline transition-all duration-200 hover:-translate-y-0.5 mt-auto ${plan.btnClass}`}
                    style={plan.btnStyle as React.CSSProperties}
                  >
                    {isUpgrade ? "Upgrade" : plan.btnLabel}
                  </Link>
                )}
              </TiltCard>
            );
          })}
        </StaggerReveal>
      </div>
    </section>
  );
}
